import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import ResizableImage from "@/components/ResizableImage";
import Youtube from "@tiptap/extension-youtube";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  HelpCircle,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Quote,
  Redo,
  Settings,
  Sparkles,
  Strikethrough,
  Undo,
  Video as VideoIcon,
  Youtube as YoutubeIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

// Lazy-load the expand modal to break the mutual import cycle at parse time
// (SmartRichTextModal renders SmartRichText internally).
const SmartRichTextModal = lazy(() => import("@/components/SmartRichTextModal"));

function SlashMenu({ slash, items, containerWidth, testid, onHover, onSelect }) {
  const selectedIdx = Math.min(slash.index, items.length - 1);
  const left = Math.max(4, Math.min(slash.x, containerWidth - 260));
  return (
    <div
      className="absolute z-30 w-64 max-h-72 overflow-y-auto border border-border bg-background shadow-lg"
      style={{ left, top: slash.y }}
      onMouseDown={(e) => e.preventDefault()}
      data-testid={`${testid}-slash-menu`}
    >
      <div className="px-3 py-1.5 border-b border-border text-[10px] uppercase tracking-widest text-foreground/50 flex items-center justify-between">
        <span>Insert{slash.query ? ` · ${slash.query}` : ""}</span>
        <span>{items.length}</span>
      </div>
      {items.map((a, i) => {
        const IconEl = a.icon;
        return (
          <button
            key={a.id}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onSelect(a); }}
            onMouseEnter={() => onHover(i)}
            className={`w-full flex items-start gap-2 px-3 py-2 text-left text-xs hover:bg-muted ${i === selectedIdx ? "bg-muted" : ""}`}
            data-testid={`${testid}-slash-item-${a.id}`}
          >
            <IconEl className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong className="block leading-tight">{a.label}</strong>
              <span className="text-foreground/60">{a.hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// R69b · Inline mention picker rendered at the caret when the user types "@".
function MentionMenu({ mention, items, containerWidth, testid, onHover, onSelect }) {
  const selectedIdx = Math.min(mention.index, items.length - 1);
  const left = Math.max(4, Math.min(mention.x, containerWidth - 240));
  return (
    <div
      className="absolute z-30 w-60 max-h-64 overflow-y-auto border border-border bg-background shadow-lg"
      style={{ left, top: mention.y }}
      onMouseDown={(e) => e.preventDefault()}
      data-testid={`${testid}-mention-menu`}
    >
      <div className="px-3 py-1.5 border-b border-border text-[10px] uppercase tracking-widest text-foreground/50 flex items-center justify-between">
        <span>Mention{mention.query ? ` · ${mention.query}` : ""}</span>
        <span>{items.length}</span>
      </div>
      {items.map((m, i) => (
        <button
          key={m.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onSelect(m); }}
          onMouseEnter={() => onHover(i)}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted ${i === selectedIdx ? "bg-muted" : ""}`}
          data-testid={`${testid}-mention-item-${m.id}`}
        >
          <span className="w-5 h-5 border border-border bg-muted text-[10px] font-bold uppercase flex items-center justify-center shrink-0">
            {((m.display || m.label) || "?").slice(0, 1)}
          </span>
          <span className="truncate">{m.display || m.label}</span>
          {m.sub && <span className="ml-auto text-[10px] text-muted-foreground truncate max-w-[80px]">{m.sub}</span>}
        </button>
      ))}
      <div className="px-3 py-1.5 border-t border-border bg-muted/30 text-[9px] text-muted-foreground">
        ↑↓ to navigate · Enter to pick · Esc to close
      </div>
    </div>
  );
}


/**
 * SmartRichText — the workspace-wide, opt-in Rich Text editor.
 *
 * Design goals (Iter 115):
 *   • Toolbar is HIDDEN by default and floats above the field when focused —
 *     keeps forms visually clean.
 *   • Default height ≈ 2 text lines. Grows with content up to `maxHeight`, then
 *     scrolls inside. Never needs manual resize.
 *   • Per-field media control via `mediaMode`:
 *       "off"       → text + link only (safest, forms/notes)
 *       "url-only"  → + image URL + YouTube embed URL (no uploads)
 *       "upload"    → + drag/drop + paste + toolbar upload of images/videos
 *                     to Emergent Object Storage. Requires `workspaceId`.
 *
 * Props:
 *   value           HTML string
 *   onChange        (html) => void
 *   placeholder     text shown when empty
 *   mediaMode       "off" | "url-only" | "upload"   (default: "off")
 *   workspaceId     required when mediaMode === "upload"
 *   minRows         approx starting line-height rows (default: 2)
 *   maxHeight       px cap before scroll (default: 500)
 *   testid          data-testid on root
 *   disabled        read-only
 *   help            optional help text — renders a ? tooltip next to the toolbar
 *   allowAiImprove  enable the `/ai` slash action (default: true when Emergent key configured)
 *   onAutoSave      optional async (html) => Promise<void> — when provided,
 *                   dirty edits auto-save after `autoSaveDebounceMs` (default 3s)
 *                   and a "Saved · Xs ago" indicator appears in the toolbar
 *   autoSaveDebounceMs   default 3000
 *   inModal         internal — set true when rendered inside the expand-modal
 */
export default function SmartRichText({
  value = "",
  onChange,
  placeholder = "Write here…",
  mediaMode = "off",
  workspaceId = null,
  minRows = 2,
  maxHeight = 500,
  testid = "smart-rich-text",
  disabled = false,
  help = null,
  allowAiImprove = true,
  onAutoSave = null,
  autoSaveDebounceMs = 3000,
  inModal = false,
  // R69b · Inline @mention support. When `mentionSuggestions` is supplied
  // (array of {id, label}), typing "@" opens a picker floating at the caret
  // that filters as the user types "@query". Selecting an item replaces
  // "@query" with the label text and invokes `onMentionPick(id, label)`.
  mentionSuggestions = null,
  onMentionPick = null,
}) {
  const [focused, setFocused] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slash, setSlash] = useState(null); // { from, to, query, x, y, index }
  const [mention, setMention] = useState(null); // R69b · same shape as slash
  const [expanded, setExpanded] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [effectiveMax, setEffectiveMax] = useState(maxHeight);
  const [effectiveMedia, setEffectiveMedia] = useState(mediaMode);
  const [aiBusy, setAiBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | "dirty" | "saving" | "saved" | "error"
  const [savedAt, setSavedAt] = useState(null); // Date
  const [savedAgo, setSavedAgo] = useState(""); // "2s", "1m", etc.
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const containerRef = useRef(null);
  const slashRef = useRef(null); // live ref so keydown sees latest state
  slashRef.current = slash;
  const mentionRef = useRef(null);
  mentionRef.current = mention;
  const mentionSuggestionsRef = useRef(mentionSuggestions);
  mentionSuggestionsRef.current = mentionSuggestions;
  const onMentionPickRef = useRef(onMentionPick);
  onMentionPickRef.current = onMentionPick;
  const savedValueRef = useRef(value); // last-persisted value for dirty compare
  const autoSaveTimerRef = useRef(null);

  const canUpload = effectiveMedia === "upload" && !!workspaceId;
  const canUrlMedia = effectiveMedia === "url-only" || canUpload;

  const uploadFile = useCallback(async (file, kind /* "image" | "video" */) => {
    if (!canUpload || !workspaceId) return null;
    const isImage = (file.type || "").startsWith("image/");
    const isVideo = (file.type || "").startsWith("video/");
    if (kind === "image" && !isImage) {
      toast.error("Not an image file");
      return null;
    }
    if (kind === "video" && !isVideo) {
      toast.error("Not a video file");
      return null;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/workspaces/${workspaceId}/rich-media`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return { url: data.url, kind: data.kind };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(msg);
      return null;
    } finally {
      setUploading(false);
    }
  }, [canUpload, workspaceId]);

  const editorRef = useRef(null);

  // AI improve — sends the selected paragraph (or full text if empty selection)
  // to /api/v1/ai/improve-writing and replaces the range with the response.
  const aiImprove = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed) return;
    let from = ed.state.selection.from;
    let to = ed.state.selection.to;
    if (from === to) {
      // No selection — pick the current paragraph.
      const $pos = ed.state.doc.resolve(from);
      from = $pos.start($pos.depth);
      to = $pos.end($pos.depth);
    }
    const text = ed.state.doc.textBetween(from, to, "\n", " ").trim();
    if (!text) { toast.error("Nothing to improve — write something first."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/improve-writing", { text });
      const improved = (data?.improved || "").trim();
      if (!improved) { toast.error("AI did not return a rewrite."); return; }
      ed.chain().focus().deleteRange({ from, to }).insertContent(improved).run();
      toast.success("Rewritten by AI");
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI rewrite failed"));
    } finally {
      setAiBusy(false);
    }
  }, []);

  // ── Slash-command actions ────────────────────────────────────────────────
  // Each `run(editor)` receives a chain already .focus()'d and the slash text
  // already deleted (from..to). Ordered by relevance.
  const slashActions = [
    { id: "h2",     label: "Heading 2",     hint: "Section heading",  icon: Heading2,    keywords: ["h2", "heading", "title"], run: (ed) => ed.chain().focus().toggleHeading({ level: 2 }).run() },
    { id: "h3",     label: "Heading 3",     hint: "Sub-heading",       icon: Heading3,    keywords: ["h3", "heading", "subheading"], run: (ed) => ed.chain().focus().toggleHeading({ level: 3 }).run() },
    { id: "bullet", label: "Bulleted list", hint: "Unordered list",    icon: List,        keywords: ["bullet", "list", "ul"], run: (ed) => ed.chain().focus().toggleBulletList().run() },
    { id: "ordered", label: "Numbered list", hint: "Ordered list",     icon: ListOrdered, keywords: ["number", "ordered", "ol", "list"], run: (ed) => ed.chain().focus().toggleOrderedList().run() },
    { id: "quote",  label: "Quote",          hint: "Blockquote",        icon: Quote,       keywords: ["quote", "blockquote"], run: (ed) => ed.chain().focus().toggleBlockquote().run() },
    { id: "code",   label: "Code block",     hint: "Monospaced code",   icon: Code,        keywords: ["code", "pre"], run: (ed) => ed.chain().focus().toggleCodeBlock().run() },
    { id: "divider", label: "Divider",       hint: "Horizontal rule",   icon: Minus,       keywords: ["divider", "hr", "rule", "line"], run: (ed) => ed.chain().focus().setHorizontalRule().run() },
    { id: "link",   label: "Link",           hint: "Insert hyperlink",  icon: LinkIcon,    keywords: ["link", "url", "href"], run: (ed) => {
        const url = window.prompt("URL", "https://");
        if (url) ed.chain().focus().setLink({ href: url }).insertContent(url).run();
      } },
    ...(canUrlMedia ? [
      { id: "image-url", label: "Image by URL", hint: "Embed an image URL", icon: ImageIcon, keywords: ["image", "img", "url"], run: (ed) => {
        const url = window.prompt("Image URL (https://…)");
        if (url) ed.chain().focus().setImage({ src: url }).run();
      } },
      { id: "youtube", label: "YouTube",       hint: "Embed a YouTube video", icon: YoutubeIcon, keywords: ["youtube", "video", "yt"], run: (ed) => {
        const url = window.prompt("YouTube URL");
        if (url) ed.chain().focus().setYoutubeVideo({ src: url }).run();
      } },
    ] : []),
    ...(canUpload ? [
      { id: "upload-image", label: "Upload image", hint: "From your device", icon: ImageIcon, keywords: ["upload", "image", "photo", "picture"], run: () => fileInputRef.current?.click() },
      { id: "upload-video", label: "Upload video", hint: "From your device", icon: VideoIcon, keywords: ["upload", "video", "movie"], run: () => videoInputRef.current?.click() },
    ] : []),
    ...(allowAiImprove ? [
      { id: "ai-improve", label: "AI · Improve writing", hint: "Rewrite the selection with Claude", icon: Sparkles, keywords: ["ai", "improve", "rewrite", "polish", "claude"], run: () => aiImprove() },
    ] : []),
  ];

  const filteredSlashActions = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return slashActions;
    return slashActions.filter((a) => a.label.toLowerCase().includes(q) || a.keywords.some((k) => k.includes(q)));
  };

  const runSlashAction = (action) => {
    const ed = editorRef.current;
    const state = slashRef.current;
    if (!ed || !state) return;
    ed.chain().focus().deleteRange({ from: state.from, to: state.to }).run();
    action.run(ed);
    setSlash(null);
  };

  // R69b · Filter suggestions against the current "@query" and pick.
  // Cap at 12 rows so the popup stays compact; users refine with more
  // characters if their workspace is huge.
  const filteredMentionItems = (query) => {
    const list = mentionSuggestionsRef.current || [];
    const q = (query || "").trim().toLowerCase();
    if (!q) return list.slice(0, 12);
    // R138x · Match on either display name or label so a `@task:<uuid>`
    // paste + a readable-title search both work.
    return list.filter((m) => {
      const hay = ((m.display || "") + " " + (m.label || "") + " " + (m.sub || "")).toLowerCase();
      return hay.includes(q);
    }).slice(0, 12);
  };

  const runMentionPick = (item) => {
    const ed = editorRef.current;
    const state = mentionRef.current;
    if (!ed || !state || !item) return;
    // R138x · Use `insert` when provided (e.g. `task:<uuid>` marker) so
    // consumers can round-trip the picked entity back through a parser.
    const insertText = item.insert || item.label;
    ed.chain()
      .focus()
      .deleteRange({ from: state.from, to: state.to })
      .insertContent(`@${insertText} `)
      .run();
    setMention(null);
    try { onMentionPickRef.current?.(item.id, item.label); } catch { /* ignore */ }
  };

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-primary underline" } }),
      Placeholder.configure({ placeholder }),
      ResizableImage.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "max-w-full h-auto my-3 rounded" } }),
      Youtube.configure({ width: 640, height: 360, allowFullscreen: true, HTMLAttributes: { class: "my-3 max-w-full" } }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      if (ed.isDestroyed) return;
      try {
        onChange?.(ed.isEmpty ? "" : ed.getHTML());
      } catch {
        /* editor being torn down during a re-render — safe to ignore */
      }
      // Slash-command detection: look for "/query" at the caret with no space
      // between the "/" and the caret. The "/" must be at line start or preceded
      // by whitespace so we don't hijack URLs/dates.
      try {
        const { state } = ed;
        const { selection } = state;
        if (!selection.empty) {
          if (slashRef.current) setSlash(null);
          if (mentionRef.current) setMention(null);
          return;
        }
        const pos = selection.from;
        const parent = selection.$from.parent;
        const parentStart = selection.$from.start();
        const textBefore = parent.textBetween(0, pos - parentStart, "\n", " ");
        // R69b · @mention detection (only when caller enabled mention picker).
        if (mentionSuggestionsRef.current) {
          const mm = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(textBefore);
          if (mm) {
            const query = mm[2];
            const triggerLen = mm[0].length - (mm[1] ? mm[1].length : 0); // length of "@query"
            const from = pos - triggerLen;
            const to = pos;
            const coords = ed.view.coordsAtPos(pos);
            const containerRect = containerRef.current?.getBoundingClientRect();
            const x = containerRect ? (coords.left - containerRect.left) : 0;
            const y = containerRect ? (coords.bottom - containerRect.top + 4) : 0;
            setMention({ from, to, query, x, y, index: 0 });
            if (slashRef.current) setSlash(null);
            return;
          }
          if (mentionRef.current) setMention(null);
        }
        // Match "/word" (or lone "/") at end, preceded by SOL or whitespace.
        const m = /(^|\s)\/([A-Za-z0-9_-]*)$/.exec(textBefore);
        if (!m) { if (slashRef.current) setSlash(null); return; }
        const query = m[2];
        const triggerLen = m[0].length - (m[1] ? m[1].length : 0); // length of "/query"
        const from = pos - triggerLen;
        const to = pos;
        const coords = ed.view.coordsAtPos(pos);
        const containerRect = containerRef.current?.getBoundingClientRect();
        const x = containerRect ? (coords.left - containerRect.left) : 0;
        const y = containerRect ? (coords.bottom - containerRect.top + 4) : 0;
        setSlash({ from, to, query, x, y, index: 0 });
      } catch {
        // any parsing hiccup — close the menu
        if (slashRef.current) setSlash(null);
        if (mentionRef.current) setMention(null);
      }
    },
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false);
      // Close slash menu when field loses focus (with a tiny delay so clicks
      // inside the menu can still fire).
      setTimeout(() => {
        if (!containerRef.current?.contains(document.activeElement)) {
          setSlash(null);
          setMention(null);
        }
      }, 150);
    },
    editorProps: {
      handleKeyDown: (view, event) => {
        // R69b · Mention menu handling takes precedence when active.
        const men = mentionRef.current;
        if (men) {
          const mItems = filteredMentionItems(men.query);
          if (event.key === "Escape") { setMention(null); return true; }
          if (mItems.length === 0) return false;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setMention({ ...men, index: (men.index + 1) % mItems.length });
            return true;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setMention({ ...men, index: (men.index - 1 + mItems.length) % mItems.length });
            return true;
          }
          if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            runMentionPick(mItems[Math.min(men.index, mItems.length - 1)]);
            return true;
          }
          return false;
        }
        const s = slashRef.current;
        if (!s) return false;
        const items = filteredSlashActions(s.query);
        if (items.length === 0) {
          if (event.key === "Escape") { setSlash(null); return true; }
          return false;
        }
        if (event.key === "Escape") { setSlash(null); return true; }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSlash({ ...s, index: (s.index + 1) % items.length });
          return true;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSlash({ ...s, index: (s.index - 1 + items.length) % items.length });
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          const action = items[Math.min(s.index, items.length - 1)];
          if (action) runSlashAction(action);
          return true;
        }
        return false;
      },
      handleDrop: (view, event) => {
        if (!canUpload) return false;
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length === 0) return false;
        const first = files[0];
        const kind = (first.type || "").startsWith("video/") ? "video" : "image";
        event.preventDefault();
        (async () => {
          const res = await uploadFile(first, kind);
          if (!res) return;
          if (kind === "image") {
            editor?.chain().focus().setImage({ src: res.url }).run();
          } else {
            // Insert an HTML5 video block for uploaded video.
            editor?.chain().focus().insertContent(
              `<p><video controls src="${res.url}" style="max-width:100%;height:auto" /></p>`,
            ).run();
          }
        })();
        return true;
      },
      handlePaste: (view, event) => {
        if (!canUpload) return false;
        const items = Array.from(event.clipboardData?.items || []);
        const fileItem = items.find((it) => it.kind === "file" && (it.type.startsWith("image/") || it.type.startsWith("video/")));
        if (!fileItem) return false;
        const file = fileItem.getAsFile();
        if (!file) return false;
        event.preventDefault();
        const kind = file.type.startsWith("video/") ? "video" : "image";
        (async () => {
          const res = await uploadFile(file, kind);
          if (!res) return;
          if (kind === "image") {
            editor?.chain().focus().setImage({ src: res.url }).run();
          } else {
            editor?.chain().focus().insertContent(
              `<p><video controls src="${res.url}" style="max-width:100%;height:auto" /></p>`,
            ).run();
          }
        })();
        return true;
      },
    },
  });

  // Sync external value updates (rare — e.g., form reset)
  useEffect(() => {
    if (!editor) return;
    editorRef.current = editor;
    if (editor.isDestroyed) return;
    try {
      if (value !== editor.getHTML() && !editor.isFocused) {
        editor.commands.setContent(value || "", false);
      }
    } catch {
      /* editor teardown race — ignore */
    }
  }, [value, editor]);

  // Auto-save (opt-in): when `onAutoSave` is supplied, debounce and persist.
  useEffect(() => {
    if (!onAutoSave) return undefined;
    if (value === savedValueRef.current) {
      if (saveStatus === "dirty") setSaveStatus("saved"); // rare: value reverted
      return undefined;
    }
    setSaveStatus("dirty");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await onAutoSave(value);
        savedValueRef.current = value;
        setSavedAt(new Date());
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      }
    }, Math.max(500, autoSaveDebounceMs));
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [value, onAutoSave, autoSaveDebounceMs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick the "Saved · Xs ago" label every 15s while a savedAt exists.
  useEffect(() => {
    if (!savedAt) { setSavedAgo(""); return undefined; }
    const fmt = () => {
      const secs = Math.max(1, Math.round((Date.now() - savedAt.getTime()) / 1000));
      if (secs < 60) return `${secs}s ago`;
      const mins = Math.round(secs / 60);
      if (mins < 60) return `${mins}m ago`;
      return `${Math.round(mins / 60)}h ago`;
    };
    setSavedAgo(fmt());
    const id = setInterval(() => setSavedAgo(fmt()), 15000);
    return () => clearInterval(id);
  }, [savedAt]);

  const addLink = () => {
    const url = window.prompt("URL", editor?.getAttributes("link").href || "https://");
    if (url === null) return;
    if (url === "") { editor?.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImageUrl = () => {
    const url = window.prompt("Image URL (https://…)");
    if (!url) return;
    editor?.chain().focus().setImage({ src: url }).run();
  };

  const addYoutube = () => {
    const url = window.prompt("YouTube URL");
    if (!url) return;
    editor?.chain().focus().setYoutubeVideo({ src: url }).run();
  };

  if (!editor) return null;

  // eslint-disable-next-line react/no-unstable-nested-components
  const Btn = ({ on, action, icon: Icon, label, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={action}
      title={label}
      className={`px-1.5 py-1 hover:bg-muted transition ${on ? "bg-muted" : ""}`}
      data-testid={tid}
    >
      <Icon className="w-3 h-3" />
    </button>
  );

  // Approx height for `minRows` × 1.4 line-height × 14px text.
  const minHeightPx = Math.max(20, Math.round(minRows * 22));

  return (
    <>
    <div
      ref={containerRef}
      className={`relative border transition-colors ${focused ? "border-primary" : "border-border"} ${disabled ? "bg-muted/30" : "bg-background"}`}
      data-testid={testid}
      onMouseDown={() => editor.commands.focus()}
    >
      {focused && !disabled && (
        <div
          className="flex items-stretch overflow-x-auto border-b border-border bg-muted/30 animate-in fade-in slide-in-from-top-1 duration-150 no-scrollbar"
          onMouseDown={(e) => e.preventDefault()}
          data-testid={`${testid}-toolbar`}
        >
          <Btn on={editor.isActive("bold")} action={() => editor.chain().focus().toggleBold().run()} icon={Bold} label="Bold (⌘B)" tid={`${testid}-bold`} />
          <Btn on={editor.isActive("italic")} action={() => editor.chain().focus().toggleItalic().run()} icon={Italic} label="Italic (⌘I)" tid={`${testid}-italic`} />
          <Btn on={editor.isActive("strike")} action={() => editor.chain().focus().toggleStrike().run()} icon={Strikethrough} label="Strikethrough" tid={`${testid}-strike`} />
          <Btn on={editor.isActive("bulletList")} action={() => editor.chain().focus().toggleBulletList().run()} icon={List} label="Bulleted list" tid={`${testid}-bullet`} />
          <Btn on={editor.isActive("orderedList")} action={() => editor.chain().focus().toggleOrderedList().run()} icon={ListOrdered} label="Numbered list" tid={`${testid}-ordered`} />
          <Btn on={editor.isActive("blockquote")} action={() => editor.chain().focus().toggleBlockquote().run()} icon={Quote} label="Quote" tid={`${testid}-quote`} />
          <Btn on={editor.isActive("code")} action={() => editor.chain().focus().toggleCode().run()} icon={Code} label="Inline code" tid={`${testid}-code`} />
          <Btn on={editor.isActive("link")} action={addLink} icon={LinkIcon} label="Link" tid={`${testid}-link`} />
          {canUpload && (
            <>
              <Btn on={false} action={() => fileInputRef.current?.click()} icon={ImageIcon} label="Upload image" tid={`${testid}-upload-image`} />
              <Btn on={false} action={() => videoInputRef.current?.click()} icon={VideoIcon} label="Upload video" tid={`${testid}-upload-video`} />
            </>
          )}
          {!canUpload && canUrlMedia && (
            <Btn on={false} action={addImageUrl} icon={ImageIcon} label="Image by URL" tid={`${testid}-image-url`} />
          )}
          {canUrlMedia && (
            <Btn on={false} action={addYoutube} icon={YoutubeIcon} label="Embed YouTube" tid={`${testid}-youtube`} />
          )}
          <div className="ml-auto flex items-stretch shrink-0">
            {(uploading || aiBusy || saveStatus) && (
              <span className="flex items-center px-1.5 text-[10px] text-foreground/60 whitespace-nowrap">
                {aiBusy ? (<><Loader2 className="w-3 h-3 animate-spin mr-1" /> AI…</>) :
                 uploading ? (<><Loader2 className="w-3 h-3 animate-spin mr-1" /> Uploading…</>) :
                 saveStatus === "saving" ? (<><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving…</>) :
                 saveStatus === "saved" ? <span className="text-emerald-600 dark:text-emerald-400">Saved{savedAgo ? ` · ${savedAgo}` : ""}</span> :
                 saveStatus === "dirty" ? <span className="text-amber-600 dark:text-amber-400">Unsaved</span> :
                 saveStatus === "error" ? <span className="text-destructive">Save failed</span> :
                 null}
              </span>
            )}
            {help && (
              <Btn on={showHelp} action={() => setShowHelp((v) => !v)} icon={HelpCircle} label="Help" tid={`${testid}-help`} />
            )}
            <Btn on={showConfig} action={() => setShowConfig((v) => !v)} icon={Settings} label="Configure" tid={`${testid}-config`} />
            {!inModal && (
              <Btn on={false} action={() => setExpanded(true)} icon={Maximize2} label="Expand" tid={`${testid}-expand`} />
            )}
            <Btn on={false} action={() => editor.chain().focus().undo().run()} icon={Undo} label="Undo" tid={`${testid}-undo`} />
            <Btn on={false} action={() => editor.chain().focus().redo().run()} icon={Redo} label="Redo" tid={`${testid}-redo`} />
          </div>
        </div>
      )}

      <div
        className="overflow-y-auto smart-rich-text-scroller"
        style={{ minHeight: minHeightPx, maxHeight: effectiveMax }}
      >
        <EditorContent
          editor={editor}
          className="px-2.5 py-1 text-sm smart-rich-text-content"
        />
      </div>

      {/* Slash-command popover */}
      {slash && focused && filteredSlashActions(slash.query).length > 0 && (
        <SlashMenu
          slash={slash}
          items={filteredSlashActions(slash.query)}
          containerWidth={containerRef.current?.clientWidth || 300}
          testid={testid}
          onHover={(i) => setSlash((s) => (s ? { ...s, index: i } : s))}
          onSelect={runSlashAction}
        />
      )}

      {/* R69b · @mention picker */}
      {mention && focused && mentionSuggestions && filteredMentionItems(mention.query).length > 0 && (
        <MentionMenu
          mention={mention}
          items={filteredMentionItems(mention.query)}
          containerWidth={containerRef.current?.clientWidth || 300}
          testid={testid}
          onHover={(i) => setMention((s) => (s ? { ...s, index: i } : s))}
          onSelect={runMentionPick}
        />
      )}

      {/* Hidden file inputs for upload buttons */}
      {canUpload && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              const res = await uploadFile(file, "image");
              if (!res) return;
              editor.chain().focus().setImage({ src: res.url }).run();
            }}
            data-testid={`${testid}-image-input`}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              const res = await uploadFile(file, "video");
              if (!res) return;
              editor.chain().focus().insertContent(
                `<p><video controls src="${res.url}" style="max-width:100%;height:auto" /></p>`,
              ).run();
            }}
            data-testid={`${testid}-video-input`}
          />
        </>
      )}

      {/* Config popover */}
      {showConfig && (
        <div className="absolute right-1 z-30 border border-border bg-background shadow-lg p-3 text-xs w-56 top-9" onMouseDown={(e) => e.preventDefault()} data-testid={`${testid}-config-panel`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold">Field settings</span>
            <button type="button" onClick={() => setShowConfig(false)} className="text-foreground/60 hover:text-foreground" data-testid={`${testid}-config-close`}>✕</button>
          </div>
          <div className="space-y-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-foreground/60">Max height</span>
              <select value={effectiveMax} onChange={(e) => setEffectiveMax(Number(e.target.value))} className="mt-0.5 w-full border border-border bg-background px-1.5 py-1 text-xs" data-testid={`${testid}-config-max`}>
                <option value={220}>Short (220px)</option>
                <option value={500}>Default (500px)</option>
                <option value={900}>Tall (900px)</option>
                <option value={99999}>No limit</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-foreground/60">Media</span>
              <select value={effectiveMedia} onChange={(e) => setEffectiveMedia(e.target.value)} className="mt-0.5 w-full border border-border bg-background px-1.5 py-1 text-xs" data-testid={`${testid}-config-media`}>
                <option value="off">Text + links only</option>
                <option value="url-only">+ Image URL &amp; YouTube</option>
                <option value="upload" disabled={!workspaceId}>+ Upload images / videos</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {/* Inline help popover */}
      {showHelp && help && (
        <div className="absolute right-1 z-30 border border-border bg-background shadow-lg p-3 text-xs w-72 top-9" onMouseDown={(e) => e.preventDefault()} data-testid={`${testid}-help-panel`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-semibold flex items-center gap-1"><HelpCircle className="w-3 h-3" /> Help</span>
            <button type="button" onClick={() => setShowHelp(false)} className="text-foreground/60 hover:text-foreground">✕</button>
          </div>
          <div className="text-foreground/80 whitespace-pre-line">{help}</div>
        </div>
      )}
    </div>
    {/* Expand-to-modal (lazy-loaded to avoid mutual import) */}
    {expanded && !inModal && (
      <Suspense fallback={null}>
        <SmartRichTextModal
          title="Edit"
          initialValue={value}
          placeholder={placeholder}
          mediaMode={effectiveMedia}
          workspaceId={workspaceId}
          help={help}
          allowAiImprove={allowAiImprove}
          onSave={(html) => { onChange?.(html); setExpanded(false); }}
          onClose={() => setExpanded(false)}
        />
      </Suspense>
    )}
    </>
  );
}

/** Read-only renderer that safely displays SmartRichText HTML.
 *  Whitelisted iframes: YouTube, Vimeo. `<video>` retained for uploaded media. */
export function SmartRichTextDisplay({ html, className = "", testid }) {
  const [cleanHtml, setCleanHtml] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!html) { setCleanHtml(""); return; }
    import("dompurify").then((mod) => {
      if (cancelled) return;
      const DOMPurify = mod.default || mod;
      DOMPurify.addHook("uponSanitizeElement", (node, data) => {
        if (data.tagName === "iframe") {
          const src = (node.getAttribute && node.getAttribute("src")) || "";
          const ok = /^https:\/\/(www\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com|player\.vimeo\.com)\//i.test(src);
          if (!ok) node.parentNode?.removeChild(node);
        }
      });
      setCleanHtml(DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_TAGS: ["iframe", "video", "source"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src", "width", "height", "controls", "poster", "type"],
      }));
      DOMPurify.removeAllHooks();
    });
    return () => { cancelled = true; };
  }, [html]);
  if (!html) return null;
  return (
    <div
      data-testid={testid}
      className={`prose prose-sm max-w-none dark:prose-invert text-sm smart-rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
    />
  );
}
